import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtINRCompact } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import VaultBrief from "@/components/VaultBrief";
import VaultHealthCard from "@/components/VaultHealthCard";
import MoneyMap from "@/components/MoneyMap";
import ForexTicker from "@/components/ForexTicker";
import {
    Activity, PieChart, CreditCard, Wallet, ShieldCheck, ArrowRight,
    Sparkles, Plane, Repeat, FolderLock, Users as UsersIcon, Compass,
    Bell, Clock,
} from "lucide-react";

/**
 * VAULT Command Centre V2 — founder-gated (see /lib/dashboardV2Flag).
 *
 * Composes EXISTING live components: VaultBrief, VaultHealthCard, MoneyMap,
 * ForexTicker. Pulls real tenant data from existing endpoints — never
 * fabricates. Contextual sections collapse to compact CTAs when the user
 * has no data yet (e.g. no travel → small "Add a trip" pill).
 *
 * Nothing here duplicates business logic; every card deep-links to the
 * canonical module.
 */

function greeting(name) {
    const h = new Date().getHours();
    const part = h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
    return `${part}${name ? `, ${name.split(" ")[0]}` : ""}`;
}

function Metric({ icon: Icon, label, value, sub, to, tone = "neutral", testId }) {
    const ring = {
        healthy:  "ring-emerald-100 bg-emerald-50/40",
        warning:  "ring-amber-100 bg-amber-50/40",
        critical: "ring-rose-100 bg-rose-50/40",
        neutral:  "ring-stone-100 bg-white",
    }[tone];
    return (
        <Link
            to={to}
            className={`group rounded-2xl ring-1 ${ring} p-4 hover:shadow-md hover:-translate-y-0.5 transition-all block`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} strokeWidth={1.8} className="text-stone-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{label}</span>
            </div>
            <div className="text-2xl font-semibold text-stone-900 tabular-nums leading-none">{value ?? "—"}</div>
            {sub && <div className="text-[11px] text-stone-500 mt-1.5">{sub}</div>}
        </Link>
    );
}

function CompactCta({ icon: Icon, title, body, cta, to, testId }) {
    return (
        <Link
            to={to}
            className="rounded-2xl border border-stone-200 bg-white p-4 hover:border-amber-400 hover:bg-amber-50/30 transition-all flex items-start gap-3 group"
            data-testid={testId}
        >
            <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0 group-hover:bg-amber-100 transition-colors">
                <Icon size={15} className="text-stone-700" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-stone-900">{title}</div>
                <div className="text-[12px] text-stone-500 leading-snug mt-0.5">{body}</div>
                <div className="text-[11.5px] font-semibold text-amber-700 mt-1.5 inline-flex items-center gap-1">
                    {cta} <ArrowRight size={11} />
                </div>
            </div>
        </Link>
    );
}

function SectionHeader({ eyebrow, title, subtitle, action, actionTo, testId }) {
    return (
        <div className="flex items-baseline justify-between mb-3" data-testid={testId}>
            <div>
                {eyebrow && <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{eyebrow}</div>}
                <h2 className="text-base font-semibold text-stone-900 mt-0.5">{title}</h2>
                {subtitle && <div className="text-[12px] text-stone-500 mt-0.5">{subtitle}</div>}
            </div>
            {action && (
                <Link to={actionTo} className="text-[12px] font-semibold text-stone-700 hover:text-amber-700 inline-flex items-center gap-1">
                    {action} <ArrowRight size={12} />
                </Link>
            )}
        </div>
    );
}

export default function CommandCentreV2() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [ds, setDs] = useState(null);
    const [nw, setNw] = useState(null);
    const [health, setHealth] = useState(null);
    const [subs, setSubs] = useState(null);
    const [docs, setDocs] = useState(null);
    const [travel, setTravel] = useState(null);
    const [together, setTogether] = useState(null);
    const [upcoming, setUpcoming] = useState(null);

    useEffect(() => {
        // Fire ALL summary endpoints in parallel — every one is cheap.
        // Every call tolerates failure; missing data collapses to compact CTAs.
        const safe = (p) => p.then((r) => r.data).catch(() => null);
        (async () => {
            const [
                dsR, nwR, healthR, subsR, docsR, tripsR, togetherR, calR,
            ] = await Promise.all([
                safe(api.get("/analytics/dashboard/summary")),
                safe(api.get("/investments/net-worth")),
                safe(api.get("/health-score")),
                safe(api.get("/subscriptions")),
                safe(api.get("/documents")),
                safe(api.get("/forex/trips")),
                safe(api.get("/together/summary")),
                safe(api.get("/financial-calendar/upcoming?days=30")),
            ]);
            setDs(dsR);
            setNw(nwR);
            setHealth(healthR);
            setSubs(subsR);
            setDocs(docsR);
            setTravel(tripsR);
            setTogether(togetherR);
            setUpcoming(calR);
        })();
    }, []);

    const nextTrip = useMemo(() => {
        if (!Array.isArray(travel)) return null;
        const now = new Date().toISOString().slice(0, 10);
        return travel.find((t) => t.start_date && t.start_date >= now && t.status !== "Cancelled") || null;
    }, [travel]);

    const subsCount = Array.isArray(subs) ? subs.length : (subs?.items?.length || 0);
    const docsCount = Array.isArray(docs) ? docs.length : (docs?.items?.length || 0);
    const upcomingCount = Array.isArray(upcoming) ? upcoming.length : (upcoming?.events?.length || 0);
    const togetherActive = !!(together?.enabled || together?.household_id || together?.members?.length);

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="command-centre-v2">
            {/* ═══ Greeting header ═══ */}
            <header className="mb-8">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">VAULT Command Centre</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2" data-testid="cc-greeting">
                    {greeting(user?.name)}.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">Here&rsquo;s what deserves your attention today.</p>
            </header>

            {/* ═══ 1. VAULT Brief (hero) ═══ */}
            <div className="mb-8">
                <SectionHeader eyebrow="VAULT Brief" title="What matters right now" subtitle="A single, honest summary from your tenant data." testId="cc-brief-header" />
                <VaultBrief firstName={user?.name?.split(" ")[0] || "there"} />
            </div>

            {/* ═══ 2. At-a-glance metrics ═══ */}
            <div className="mb-8">
                <SectionHeader eyebrow="At a Glance" title="Where you stand" testId="cc-glance-header" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cc-metrics">
                    <Metric
                        icon={Activity}
                        label="Finance health"
                        value={health?.score != null ? `${health.score}/100` : "—"}
                        sub={health?.status ? `Status: ${health.status}` : "Live score"}
                        tone={(health?.score >= 80) ? "healthy" : (health?.score >= 60) ? "warning" : "neutral"}
                        to="/health-score"
                        testId="cc-metric-health"
                    />
                    <Metric
                        icon={PieChart}
                        label="Net worth"
                        value={nw ? fmtINRCompact(nw.net_worth || 0) : "—"}
                        sub={nw ? `${fmtINRCompact(nw.total_assets || 0)} own · ${fmtINRCompact(nw.total_liabilities || 0)} owe` : "Add assets to see net worth"}
                        tone="neutral"
                        to="/investments"
                        testId="cc-metric-networth"
                    />
                    <Metric
                        icon={CreditCard}
                        label="Card outstanding"
                        value={ds ? fmtINRCompact(ds.total_outstanding || 0) : "—"}
                        sub={ds?.active_cards ? `${ds.active_cards} active card${ds.active_cards === 1 ? "" : "s"}` : "Add a card to start"}
                        tone={(ds?.total_outstanding || 0) > 0 ? "warning" : "healthy"}
                        to="/cards"
                        testId="cc-metric-outstanding"
                    />
                    <Metric
                        icon={Wallet}
                        label="This month spend"
                        value={ds ? fmtINRCompact(ds.total_expenses_month || 0) : "—"}
                        sub="Across every card and account"
                        tone="neutral"
                        to="/expenses"
                        testId="cc-metric-spend"
                    />
                </div>
            </div>

            {/* ═══ 3. Money Flow ═══ */}
            <div className="mb-8" data-testid="cc-money-flow">
                <SectionHeader eyebrow="Money Flow" title="One elegant view of your financial world" action="Explore Money Flow" actionTo="/finance-review" testId="cc-flow-header" />
                <MoneyMap />
            </div>

            {/* ═══ 4. Currency Watch ═══ */}
            <div className="mb-8" data-testid="cc-currency-watch">
                <SectionHeader eyebrow="Currency Watch" title="Currencies you care about" subtitle="Latest FX rates — indicative. Final billing uses statement values." action="Open Live Forex" actionTo="/forex" testId="cc-fx-header" />
                <ForexTicker />
            </div>

            {/* ═══ 5. Contextual rail: Travel · Subscriptions · Documents · Together ═══ */}
            <div className="mb-8">
                <SectionHeader eyebrow="Signals" title="Where to look next" testId="cc-signals-header" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {nextTrip ? (
                        <CompactCta
                            icon={Plane}
                            title={`${nextTrip.name || "Upcoming trip"} · ${nextTrip.country || ""}`}
                            body={`${nextTrip.start_date}${nextTrip.end_date ? ` → ${nextTrip.end_date}` : ""}. Keep boarding pass and receipts organised.`}
                            cta="Open Trip"
                            to="/organise/travel"
                            testId="cc-travel-active"
                        />
                    ) : (
                        <CompactCta
                            icon={Plane}
                            title="Travel"
                            body="Keep boarding passes, forex spend and trip budgets organised in VAULT."
                            cta="Add Boarding Pass"
                            to="/organise/travel/add"
                            testId="cc-travel-empty"
                        />
                    )}
                    <CompactCta
                        icon={Repeat}
                        title="Subscriptions"
                        body={subsCount ? `${subsCount} active subscription${subsCount === 1 ? "" : "s"} tracked. Review upcoming renewals.` : "Add subscriptions once and VAULT keeps renewal dates organised."}
                        cta={subsCount ? "Review subscriptions" : "Add subscription"}
                        to="/subscriptions"
                        testId="cc-subs-cta"
                    />
                    <CompactCta
                        icon={FolderLock}
                        title="Documents"
                        body={docsCount ? `${docsCount} document${docsCount === 1 ? "" : "s"} organised. Insurance, warranties, statements.` : "Store insurance, warranties and important documents in one place."}
                        cta={docsCount ? "Open documents" : "Add document"}
                        to="/documents"
                        testId="cc-docs-cta"
                    />
                    {togetherActive ? (
                        <CompactCta
                            icon={UsersIcon}
                            title="VAULT Together"
                            body="Shared responsibilities and joint obligations — with individual privacy preserved."
                            cta="Open Together"
                            to="/together"
                            testId="cc-together-active"
                        />
                    ) : (
                        <CompactCta
                            icon={UsersIcon}
                            title="Share with a partner"
                            body="Set up VAULT Together for shared household responsibilities without giving up privacy."
                            cta="Set up Together"
                            to="/together"
                            testId="cc-together-empty"
                        />
                    )}
                </div>
            </div>

            {/* ═══ 6. What's Next + Health ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-8">
                <div className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-whats-next">
                    <SectionHeader eyebrow="What's Next" title={upcomingCount ? `${upcomingCount} item${upcomingCount === 1 ? "" : "s"} in the next 30 days` : "The next 30 days"} action="Open Calendar" actionTo="/calendar" testId="cc-next-header" />
                    {upcomingCount ? (
                        <ul className="divide-y divide-stone-100">
                            {(upcoming.events || upcoming || []).slice(0, 5).map((ev, i) => (
                                <li key={ev.id || ev.key || i} className="py-2.5 flex items-center gap-3 text-[13px]">
                                    <Clock size={13} className="text-stone-400 shrink-0" />
                                    <span className="text-stone-800 flex-1 truncate">{ev.title || ev.name || ev.label || "Upcoming"}</span>
                                    <span className="text-[11px] text-stone-500 tabular-nums whitespace-nowrap">{ev.date || ev.due_date || ""}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-[13px] text-stone-500 py-6 text-center">
                            <Sparkles size={16} className="mx-auto text-amber-500 mb-2" />
                            Nothing scheduled in the next 30 days. Your VAULT is calm.
                        </div>
                    )}
                </div>
                <VaultHealthCard />
            </div>

            {/* ═══ 7. Explore VAULT rail ═══ */}
            <div className="mb-10" data-testid="cc-explore">
                <SectionHeader eyebrow="Explore VAULT" title="Everything, organised" subtitle="Every LIVE module — click through to the canonical page." action="Open All Tools" actionTo="/tools" />
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {[
                        { label: "Cards",         to: "/cards",         icon: CreditCard },
                        { label: "Bank Accounts", to: "/bank-accounts", icon: Wallet },
                        { label: "Forex Cards",   to: "/forex-cards",   icon: Plane },
                        { label: "Expenses",      to: "/expenses",      icon: Compass },
                        { label: "Payments",      to: "/payments",      icon: ArrowRight },
                        { label: "Subscriptions", to: "/subscriptions", icon: Repeat },
                        { label: "Investments",   to: "/investments",   icon: PieChart },
                        { label: "Assets",        to: "/assets",        icon: ShieldCheck },
                        { label: "Documents",     to: "/documents",     icon: FolderLock },
                        { label: "Calendar",      to: "/calendar",      icon: Clock },
                        { label: "Forecast",      to: "/vault-forecast", icon: Activity },
                        { label: "Alerts",        to: "/alerts",        icon: Bell },
                    ].map((m) => (
                        <Link
                            key={m.to}
                            to={m.to}
                            className="rounded-xl border border-stone-200 bg-white px-3 py-2.5 flex items-center gap-2 hover:border-amber-400 hover:bg-amber-50/30 transition-all text-[12.5px] text-stone-800"
                            data-testid={`cc-explore-${m.label.toLowerCase().replace(/\s+/g,"-")}`}
                        >
                            <m.icon size={13} className="text-stone-500 shrink-0" />
                            <span className="truncate">{m.label}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Founder toggle footer */}
            <div className="text-center text-[11px] text-stone-400 mb-6">
                Command Centre v2 · founder-gated preview ·{" "}
                <button
                    onClick={() => { try { window.localStorage.removeItem("vault.dashboard_v2"); } catch { /* ignore */ } navigate("/dashboard"); }}
                    className="underline hover:text-stone-700"
                    data-testid="cc-back-to-classic"
                >
                    Switch to classic Dashboard
                </button>
            </div>
        </div>
    );
}
